import { v4 as uuid } from 'uuid';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import {
  DocumentVersion,
  CustomFieldUsage,
  PageGroupSource,
  PageGroup,
  Page,
} from '@alucio/aws-beacon-amplify/src/models';
import { DocumentORM, DocumentVersionORM, DOCUMENT_ACTIONS_ENUM } from 'src/types/types';

import { store } from 'src/state/redux';
import { canPerformAction, allDocumentsSortedAndFilteredFactory } from 'src/state/redux/selector/document';
import { tenantList } from 'src/state/redux/selector/tenant';
import { getMappedCustomValues } from 'src/state/redux/selector/common';
import { generateAllPagesForVersion } from 'src/utils/documentHelpers';
import { TabOptions } from './publisherVersioning.types';
import { ContentPageData, Recommendations } from 'src/hooks/useContentPageData/useContentPageData';
import { MATCH_SLIDE_STATUS, MappedValues, MappingType, MatchSlideStatesType } from './PublisherVersioningProvider';
import {
  IS_WARNING_THRESHOLD,
  SIMILARITY_SCORE_THRESHOLD,
  NO_RECOMMENDATION_SCORE_THRESHOLD,
} from './MatchSlides/MatchSlidesProvider';
import { GroupedTargetItems } from 'src/components/DnD/DNASingleItemDnd';
import * as logger from 'src/utils/logger';
import cloneDeep from 'lodash/cloneDeep';

export const FIRST_MATCH_SLIDE_DISPLAY_DISABLED_THRESHOLD = 0.60;
export const RECOMMENDATION_DIFFERENCE_THRESHOLD = 0.02;

interface VersionChanges {
  isMajorVersionRequired: boolean,
  isMajorVersionProposed: boolean,
}

/**
 * We cannot pass DocumentORMs around via the context or through events
 * due to it not being serializable (circular structure)
 *
 * Instead, we use Redux is a plain JS way with selectors to grab the latest ORM
 * at any given time
 */

export const getDocumentORMFactory = function (id: string): () => DocumentORM {
  const documentSelector = allDocumentsSortedAndFilteredFactory()
  const filter = { filter: { model: { id } } }

  return (): DocumentORM => {
    const [targetDoc] = documentSelector(
      store.getState(),
      undefined,
      filter,
    ) as DocumentORM[]

    if (!targetDoc) throw new Error('Target Document not found')
    return targetDoc
  }
}

export const omitInternalFields = (docVer: Partial<DocumentVersion>) => {
  return omit(docVer,
    'versionNumber',
    '_version',
    '_lastChangedAt',
    '_deleted',
    'contentSource',
    'conversionStatus')
}

/**
 * Compares a working version draft against a previously published version
 * to determine what type of SemVer change should be applied
 */
export const determineSemVerChange = (
  latestDocumentVersion: Partial<DocumentVersion>,
  latestPublishedVersion?: DocumentVersionORM,
): VersionChanges => {
  const minorChange = {
    isMajorVersionRequired: false,
    isMajorVersionProposed: false,
  };

  const majorSuggestedChange = {
    isMajorVersionRequired: false,
    isMajorVersionProposed: true,
  }

  const majorRequiredChange = {
    isMajorVersionRequired: true,
    isMajorVersionProposed: true,
  }

  const draftPages = generateAllPagesForVersion(
    latestDocumentVersion.id ?? '', latestDocumentVersion.numPages ?? 0, latestDocumentVersion.pageSettings ?? [])
  const draftPageGroups = latestDocumentVersion?.pageGroups;
  const latestPublishedDocVersion = latestPublishedVersion?.model
  const latestPublishedPages = latestPublishedDocVersion ? generateAllPagesForVersion(
    latestPublishedDocVersion.id, latestPublishedDocVersion.numPages ?? 0, latestPublishedDocVersion.pageSettings) : []
  const latestPublishedPageGroups = latestPublishedVersion?.model.pageGroups

  // [NOTE] - This really shouldn't happen as pages should always be populated after file processing
  if (!latestPublishedPages || !draftPages) {
    return minorChange
  }

  // 1. A new document (first version to be published)
  //    We don't show this in the UI anyways, but might be nice to have later
  const isFirstDraft = latestDocumentVersion && !latestPublishedVersion
  if (isFirstDraft) {
    return majorRequiredChange
  }

  // 2. Draft version has fewer slides
  const draftVersionHasFewerSlides = draftPages.length < latestPublishedPages.length
  if (draftVersionHasFewerSlides) {
    return majorRequiredChange
  }

  // 3. Draft version grouping has changed due to one of
  //    a. amount of groups not matching (including not having any groups)
  //    b. slide indexes not matching
  const pageGroupLengthChanged = Number(draftPageGroups?.length) !== Number(latestPublishedPageGroups?.length)
  const draftPageGroupSlideIndexes = draftPageGroups
    ?.map(pageGroup => pageGroup.pageIds?.map(pageId => pageId.split('_')[2]))
  const publishedPageGroupSlideIndexes = latestPublishedPageGroups
    ?.map(pageGroup => pageGroup.pageIds?.map(pageId => pageId.split('_')[2]))
  const pageGroupIndexesChanged = !isEqual(draftPageGroupSlideIndexes, publishedPageGroupSlideIndexes)

  if (pageGroupLengthChanged || pageGroupIndexesChanged) {
    return majorRequiredChange
  }

  // 4. Required slides changed
  const draftRequiredSlides = draftPages?.map((page, idx) => ({ [idx]: !!page.isRequired }))
  const publishedRequiredSlides = latestPublishedPages?.map((page, idx) => ({ [idx]: !!page.isRequired }))
  // [NOTE] - We first compare the original range of slides for required changes
  const originalSlidesRequiredCheck = !isEqual(
    draftRequiredSlides.slice(0, publishedRequiredSlides.length),
    publishedRequiredSlides,
  )
  // [NOTE] - If any new additional slides have required, then flag for major change
  const additionalSlidesRequiredCheck = draftRequiredSlides
    .slice(publishedRequiredSlides.length, draftRequiredSlides.length)
    .some(slide => Object.values(slide).some(required => required))

  if (originalSlidesRequiredCheck || additionalSlidesRequiredCheck) {
    return majorRequiredChange
  }

  const {
    MSLSelectSlides: previousModifyPermission,
  } = latestPublishedVersion?.meta.permissions ?? {}

  const [currentUserTenant] = tenantList(store.getState())
  const draftConfigsMap = getMappedCustomValues(
    { internalUsages: [CustomFieldUsage.DOCUMENT] },
    latestDocumentVersion.customValues,
    currentUserTenant.config?.customFields,
  )

  // 5. Document is no longer modifiable
  const draftModifiablePermission = canPerformAction(
    DOCUMENT_ACTIONS_ENUM.modify,
    draftConfigsMap,
    currentUserTenant,
  )
  const noLongerModifiable = (!!previousModifyPermission === true && !draftModifiablePermission)

  if (noLongerModifiable) { return majorRequiredChange }

  // 6. Additional pages without other required/grouping changes
  if (draftPages.length > latestPublishedPages.length) {
    return majorSuggestedChange
  }

  // 7. Check if the linked slides have changed
  const linkedSlidesChange = draftPages.some((page, idx) => {
    const publishedPage = latestPublishedPages[idx]

    // 7b. To be backwards compatible with older records pre-linked slides schema changes, equate an undefined linkedSlides to the draft's empty array
    //     - The better fix is to make the schema non-nullable and run a migration to force all the older records to
    //       have an empty array so we can skip this edge case check
    if (!publishedPage.linkedSlides && !page.linkedSlides?.length) {
      return false
    }

    // check if two arrays are equal (strictly)
    return !isEqual(
      // the id of the pages should be removed
      page.linkedSlides?.map(e => e.split('_')[2]),
      publishedPage.linkedSlides?.map(e => e.split('_')[2]),
    )
  })

  if (linkedSlidesChange) {
    return majorRequiredChange
  }

  // 8. Otherwise anything else is a minor change
  return minorChange;

  // 8. [TODO] - Preserve user's last selection if minor change?
}

export type TabVisibility = { [key in TabOptions]?: boolean };

export const getAvailabletabs = (enableMatchSlides: boolean) => {
  const tabVisibilities: TabVisibility[] = [
    { [TabOptions.DOCUMENT_INFO_TAB]: true },
    { [TabOptions.DOCUMENT_MATCH_SLIDES_TAB]: enableMatchSlides },
    { [TabOptions.DOCUMENT_SETTINGS_TAB]: true },
    { [TabOptions.DOCUMENT_ASSOCIATED_FILES]: true },
    { [TabOptions.DOCUMENT_PUBLISH_TAB]: true },
  ]

  const availabletabs: TabOptions[] = tabVisibilities
    .filter((tabObj: TabVisibility) => Object.values(tabObj)[0])
    .map((tabObj: TabVisibility) => Object.keys(tabObj)[0] as TabOptions);

  return availabletabs
}

/**
 * Performs global greedy (https://en.wikipedia.org/wiki/Greedy_algorithm) matching for slides without mapping and with recommendations.
 *
 * Purpose:
 *   This function ensures that each slide from the previous version (recPageId) is matched to at most one new slide,
 *   and that the most relevant (highest score) matches are prioritized across all slides.
 *
 * Approach:
 *   The algorithm collects all possible (slide, recommendation) pairs and sorts them by score in descending order.
 *   It then assigns matches greedily: for each candidate, if neither the new slide nor the previous slide has been assigned,
 *   it creates the match. This ensures that the highest-scoring matches are made first, and no previous slide is matched more than once.
 *
 * Note:
 *   This is a greedy approach, which is simple and efficient for most practical cases. For optimal global assignment (maximum total score),
 *   algorithms like the Hungarian Algorithm (Kuhn-Munkres) can be used, but are more complex and often unnecessary for typical UI matching scenarios.
 *
 * @param contentPageData Array of slides to match (should be filtered to only those without mapping and with recommendations)
 * @returns Array where each index corresponds to a slide in contentPageData, and the value is the assigned previous slide pageId (or null)
 */
function greedyGlobalSlideMatching(contentPageData: ContentPageData[]): Map<string, string> {
  // Collect all possible candidates
  const candidates: { slideId: string; recPageId: string; score: number }[] = [];
  contentPageData.forEach((slide) => {
    slide.recommendations?.forEach(rec => {
      candidates.push({
        slideId: slide.pageId,
        recPageId: rec.pageId,
        score: rec.score,
      });
    });
  });
  // Sort by descending score
  candidates.sort((a, b) => b.score - a.score);
  // Keep track of assignments
  const assignedSlides = new Set<string>();
  const usedPreviousSlides = new Set<string>();
  const resultMappings = new Map<string, string>();
  // Assign matches greedily
  for (const candidate of candidates) {
    if (!assignedSlides.has(candidate.slideId) && !usedPreviousSlides.has(candidate.recPageId)) {
      resultMappings.set(candidate.slideId, candidate.recPageId);
      assignedSlides.add(candidate.slideId);
      usedPreviousSlides.add(candidate.recPageId);
    }
  }
  return resultMappings;
}

interface HandleMappingAndStatusParams {
  pageData: ContentPageData;
  mappingKey: string;
  originalRecommendations: Array<{ score: number; pageId: string }> | undefined;
  pageId: string;
}

/**
 * TODO: This function should be removed as soon BEAC_7165_avoid_duplicate_matches_in_slide_version_tracking be stable
  * This function will calculate the value of contentPageData from useContentPageData hook and return
  * a new contentPageData and a status object 'updatedMatchSlideStates' with the following rules:
  * 1. If mapping is 'USER_UNMATCHED':
      * * ContentPageData remains the same & status is NO_MATCH.
  * 2. If mapping is pageId:
      * * ContentPageData remains the same.
      * * If the mapping has been used then status is NEEDS_REVIEW else is MATCHED.
  * 3. If mapping is null/undefined:
      * * If there is no recommendation, assign mapping as 'USER_UNMATCHED' & status is NO_MATCH. (handle in the 1. rule)
      * * If the first 2 recommendations' scores have a difference of abs(x <= .005), keep mapping as null & status is NEEDS_REVIEW.
      * * Assign first recommendation (if exist and above threshold) as mapping.
        * * If the mapping has been used then status is NEEDS_REVIEW else is MATCHED.
      * * If recommendation is below threshold, keep mapping as null & status is NEEDS_REVIEW.
  * @param contentPageData the value from useContentPageData hook
  * @return an object contains updatedContentPageData and updatedMatchSlideStates
*/
export const calculateContentPageDataAndMatchSlideStates = (
  contentPageData: ContentPageData[],
  enableFirstMatchSlideDisplay: boolean,
) => {
  /**
   * A record used to track and check if the same recommendation has appeared more than once.
   * The key is the mapping (last version pageId) and the value is an array of current version pageIds.
  */
  logger
    .versioning.settings.debug('calculateContentPageDataAndMatchSlideStates featureflag BEAC_7165_ turned off');
  const mappedItemsRecord: Record<string, string[]> = {};
  const updatedMatchSlideStates: MatchSlideStatesType = {};
  const updatedContentPageData: ContentPageData[] = [];

  function updateDuplicateSlides (
    duplicateSlides: string[],
    updatedMatchSlideStates: MatchSlideStatesType,
    pageId: string,
  ) {
    duplicateSlides.forEach(duplicateSlideId => {
      const previousDuplicateSlides = updatedMatchSlideStates[duplicateSlideId]?.duplicateSlides;
      updatedMatchSlideStates[duplicateSlideId] = {
        status: MATCH_SLIDE_STATUS.NEEDS_REVIEW,
        duplicateSlides: previousDuplicateSlides ? [...previousDuplicateSlides, pageId] : [pageId],
      }
    })
  }

  contentPageData.forEach((pageData) => {
    const { mapping, recommendations: _recommendations, pageId } = pageData;
    const recommendations = filterRecommendations(
      _recommendations,
      enableFirstMatchSlideDisplay,
    );
    const isUserUnmatched = mapping === 'USER_UNMATCHED';
    const firstRecommendationId = recommendations[0]?.pageId as string | undefined;
    const noMappingAndNoRecommendations = !mapping && !firstRecommendationId;

    if (isUserUnmatched || noMappingAndNoRecommendations) {
      // Assign mapping to ContentPageData
      const newPageData = {
        ...pageData,
        mapping: 'USER_UNMATCHED',
      }
      updatedContentPageData.push(newPageData);
      // Assign status to MatchSlideStates
      updatedMatchSlideStates[pageId] = {
        status: MATCH_SLIDE_STATUS.NO_MATCH,
      };
    } else if (mapping) {
      const duplicateSlides = mappedItemsRecord[mapping];
      // Assign mapping to ContentPageData
      updatedContentPageData.push(pageData);
      // Assign status & duplicateSlides to MatchSlideStates
      updatedMatchSlideStates[pageId] = {
        status: duplicateSlides ? MATCH_SLIDE_STATUS.NEEDS_REVIEW : MATCH_SLIDE_STATUS.MATCHED,
        duplicateSlides,
      };
      // If we find this slide is a duplicate slide, we need to also update the other item's status & duplicateSlides
      if (duplicateSlides) updateDuplicateSlides(duplicateSlides, updatedMatchSlideStates, pageId)
      // Update mappedItemsRecord
      mappedItemsRecord[mapping] = duplicateSlides ? [...duplicateSlides, pageId] : [pageId]
    } else {
      // We should never get to this if block, if we do the function is broken;
      if (!firstRecommendationId) throw new Error('Cannot find first recommendation Id');
      const duplicateSlides = mappedItemsRecord[firstRecommendationId] as string[] | undefined;
      const recommendationIsBelowThreshold = recommendations[0]?.score < IS_WARNING_THRESHOLD;
      const hasMultipleRecommendations = _recommendations.length > 1;
      const scoreDifference = hasMultipleRecommendations
        ? (Math.abs(_recommendations[0].score - _recommendations[1].score)).toFixed(10)
        : 0;
      const hasSimilarRecommendations = !recommendationIsBelowThreshold &&
        hasMultipleRecommendations &&
        Number(scoreDifference) <= SIMILARITY_SCORE_THRESHOLD;
      const isMatchedStatus = !recommendationIsBelowThreshold && !hasSimilarRecommendations && !duplicateSlides;
      // Assign mapping to contentPageData: we only want to assign mapping when recommendation is not below
      // IS_WARNING_THRESHOLD and the first 2 recommendations score are not too similar
      if (!recommendationIsBelowThreshold && !hasSimilarRecommendations) {
        const newPageData = {
          ...pageData,
          mapping: firstRecommendationId,
        }
        updatedContentPageData.push(newPageData);
      } else {
        updatedContentPageData.push(pageData);
      }
      // Assign status & duplicateSlides to MatchSlideStates
      updatedMatchSlideStates[pageId] = {
        status: isMatchedStatus ? MATCH_SLIDE_STATUS.MATCHED : MATCH_SLIDE_STATUS.NEEDS_REVIEW,
        duplicateSlides,
      };
      // If we find this slide is a duplicate slide, we need to also update the other item's status & duplicateSlides
      if (duplicateSlides) updateDuplicateSlides(duplicateSlides, updatedMatchSlideStates, pageId)
      // Update mappedItemsRecord
      mappedItemsRecord[firstRecommendationId] = duplicateSlides ? [...duplicateSlides, pageId] : [pageId]
    }
  })

  return {
    updatedContentPageData,
    updatedMatchSlideStates,
  }
}

/**
  * This function will calculate the value of contentPageData from useContentPageData hook and return
  * a new contentPageData and a status object 'updatedMatchSlideStates' with the following rules:
  * 1. If mapping is 'USER_UNMATCHED':
      * * ContentPageData remains the same & status is NO_MATCH.
  * 2. If mapping is pageId:
      * * ContentPageData remains the same.
      * * If the mapping has been used then status is NEEDS_REVIEW else is MATCHED.
  * 3. If mapping is null/undefined:
      * * If there is no recommendation, assign mapping as 'USER_UNMATCHED' & status is NO_MATCH. (handle in the 1. rule)
      * * If the first 2 recommendations' scores have a difference of abs(x <= .005), keep mapping as null & status is NEEDS_REVIEW.
      * * Assign first recommendation (if exist and above threshold) as mapping.
        * * If the mapping has been used then status is NEEDS_REVIEW else is MATCHED.
      * * If recommendation is below threshold, keep mapping as null & status is NEEDS_REVIEW.
  * @param contentPageData the value from useContentPageData hook
  * @return an object contains updatedContentPageData and updatedMatchSlideStates
*/
export const calculateDedupedContentPageDataAndMatchSlideStates = (
  contentPageData: ContentPageData[],
  enableFirstMatchSlideDisplay: boolean,
) => {
  /**
   * A record used to track and check if the same recommendation has appeared more than once.
   * The key is the mapping (last version pageId) and the value is an array of current version pageIds.
  */
  logger
    .versioning.settings.debug('calculateDedupedContentPageDataAndMatchSlideStates featureflag BEAC_7165_ turned on');
  const mappedItemsRecord: Record<string, string[]> = {};
  const updatedMatchSlideStates: MatchSlideStatesType = {};
  const updatedContentPageData: ContentPageData[] = [];
  const usedPreviousPageIds = new Set<string>();
  const prevContentPageData = cloneDeep(contentPageData);

  function updateDuplicateSlides (
    duplicateSlides: string[],
    updatedMatchSlideStates: MatchSlideStatesType,
    pageId: string,
  ) {
    duplicateSlides.forEach(duplicateSlideId => {
      const previousDuplicateSlides = updatedMatchSlideStates[duplicateSlideId]?.duplicateSlides;
      updatedMatchSlideStates[duplicateSlideId] = {
        status: MATCH_SLIDE_STATUS.NEEDS_REVIEW,
        duplicateSlides: previousDuplicateSlides ? [...previousDuplicateSlides, pageId] : [pageId],
      }
    })
  }

  /**
   * Helper to assign mapping, update status, and manage duplicate slides for a page.
   */
  function handleMappingAndStatus({
    pageData,
    mappingKey,
    originalRecommendations,
    pageId,
  }: HandleMappingAndStatusParams) {
    // i need to have an array that starts from the first recommendations that matches the mappingKey, until its end
    const indexFirstValidRecommendation = originalRecommendations?.findIndex(rec => rec.pageId === mappingKey);
    const validRecommendations = originalRecommendations?.slice(indexFirstValidRecommendation);

    const duplicateSlides = mappedItemsRecord[mappingKey] as string[] | undefined;
    const recommendationIsBelowThreshold = (validRecommendations?.[0]?.score ?? 0) < IS_WARNING_THRESHOLD;

    const hasMultipleRecommendations = validRecommendations && validRecommendations.length > 1;

    const scoreDifference = hasMultipleRecommendations
      ? (Math.abs(validRecommendations[0].score - validRecommendations[1].score)).toFixed(10)
      : 0;

    const hasSimilarRecommendations = !recommendationIsBelowThreshold &&
      hasMultipleRecommendations &&
      Number(scoreDifference) <= SIMILARITY_SCORE_THRESHOLD;
    const hasMapping = !!pageData.mapping;
    const isUserUnmatched = pageData.mapping === 'USER_UNMATCHED';
    const isMatchedStatus = (hasMapping && !isUserUnmatched) ||
      (!recommendationIsBelowThreshold && !hasSimilarRecommendations && !duplicateSlides);
    // Assign mapping to contentPageData: only assign mapping when recommendation is not below threshold and not too similar
    if (!recommendationIsBelowThreshold && !hasSimilarRecommendations) {
      const newPageData = {
        ...pageData,
        mapping: mappingKey,
      };
      updatedContentPageData.push(newPageData);
    } else {
      updatedContentPageData.push(pageData);
    }
    updatedMatchSlideStates[pageId] = {
      status: isMatchedStatus ? MATCH_SLIDE_STATUS.MATCHED : MATCH_SLIDE_STATUS.NEEDS_REVIEW,
      duplicateSlides,
    };
    if (duplicateSlides) updateDuplicateSlides(duplicateSlides, updatedMatchSlideStates, pageId);
    usedPreviousPageIds.add(mappingKey);
    mappedItemsRecord[mappingKey] = duplicateSlides ? [...duplicateSlides, pageId] : [pageId];
  }

  // Global greedy matching for slides without mapping and with recommendations
  // Only for slides that do not have mapping and have recommendations
  const slidesToGloballyMatch = contentPageData.map((pageData, idx) => ({ ...pageData, _idx: idx }))
    .filter(pageData => !pageData.mapping && pageData.recommendations && pageData.recommendations.length > 0);
  const globalMatchMappings = greedyGlobalSlideMatching(slidesToGloballyMatch);
  // Map: idx in slidesToGloballyMatch -> assigned previous pageId

  /**
   * Filter recommendations already used by global matching
   *
   * Why is this necessary?
   * After performing the global greedy matching, some previous-version slides (pageIds) are already assigned as matches.
   * If we do not remove these assigned pageIds from the recommendations of other slides, the UI and any further logic
   * may incorrectly suggest or allow the user to select a slide that is no longer available for matching.
   * This ensures that each previous-version slide can only be matched once, and the recommendations always reflect
   * the true set of available options for the user, preventing confusion and maintaining data integrity.
   */
  const globallyAssignedPrevIds = new Set(Array.from(globalMatchMappings.values()));
  // also we need to add to globallyAssignedPrevIds the ids that are already mapped
  contentPageData.forEach(pageData => {
    if (pageData.mapping) {
      globallyAssignedPrevIds.add(pageData.mapping);
    }
  });

  contentPageData
    .forEach((pageData) => {
      if (pageData.recommendations) {
        const globalMatchForThisSlide = globalMatchMappings.get(pageData.pageId);
        pageData.recommendations = pageData.recommendations.filter(
          rec => {
          // If this slide has a global match, preserve that recommendation
            if (globalMatchForThisSlide === rec.pageId) {
              return true;
            }
            // For other recommendations, remove if already assigned globally
            return !globallyAssignedPrevIds.has(rec.pageId);
          },
        );
      }
    });

  contentPageData.forEach((pageData) => {
    const { mapping, pageId, recommendations: _recommendations } = pageData;
    const prevRecommendations = prevContentPageData.find(p => p.pageId === pageId)?.recommendations;

    const recommendations = filterDedupedRecommendations(
      _recommendations ?? [],
      prevRecommendations ?? [],
      enableFirstMatchSlideDisplay,
    );
    const isUserUnmatched = mapping === 'USER_UNMATCHED';
    const firstRecommendationId = recommendations[0]?.pageId as string | undefined;
    const noMappingAndNoRecommendations = !mapping && !firstRecommendationId;
    const isNoMatch = isUserUnmatched || noMappingAndNoRecommendations;

    /**
     * If this slide is in the global matching set, use that result
     */
    const matchedPrevId = globalMatchMappings.get(pageId);
    if (matchedPrevId && !isNoMatch) {
      handleMappingAndStatus({
        pageData,
        mappingKey: matchedPrevId,
        originalRecommendations: prevRecommendations,
        pageId,
      });
      return;
    }
    if (isNoMatch) {
      // Assign mapping to ContentPageData
      const newPageData = {
        ...pageData,
        mapping: 'USER_UNMATCHED',
      }
      updatedContentPageData.push(newPageData);
      // Assign status to MatchSlideStates
      updatedMatchSlideStates[pageId] = {
        status: MATCH_SLIDE_STATUS.NO_MATCH,
      };
    } else if (mapping) {
      handleMappingAndStatus({
        pageData,
        mappingKey: mapping,
        originalRecommendations: prevRecommendations,
        pageId,
      });
    } else {
      if (!firstRecommendationId) throw new Error('Cannot find first recommendation Id');
      handleMappingAndStatus({
        pageData,
        mappingKey: firstRecommendationId,
        originalRecommendations: prevRecommendations,
        pageId,
      });
    }
  })

  return {
    updatedContentPageData,
    updatedMatchSlideStates,
  }
}

/**
  * This function will calculate targetItems, determines what should be displays in the UI.
  * @param currentDocumentVersionORM
  * @param mappedValues
  * @return targetItems
*/
export const calculateDedupedDisplaySlides = (
  currentDocumentVersionORM: DocumentVersionORM,
  mappedValues: MappedValues,
  enableFirstMatchSlideDisplay: boolean,
) => {
  logger.versioning.settings.debug('calculateDisplaySlides featureflag BEAC_7165_ turned on');
  return currentDocumentVersionORM?.relations.pages.reduce<Record<string, string[]>>((acc, page) => {
    const pageId = page.model.pageId;
    const mappedPage = mappedValues[pageId] as MappingType | undefined;

    let arrayOfRecommendations: Recommendations[] = [];
    const isFirstPageSame = areFirstRecommendationsSame(mappedPage);

    if (isFirstPageSame) {
      arrayOfRecommendations = mappedPage?.rawRecommendations ?? [];
    } else {
      const firstRecommendationId = mappedPage?.recommendations?.[0]?.pageId;
      const firstValidIndex = mappedPage?.rawRecommendations.findIndex(rec => rec.pageId === firstRecommendationId);
      arrayOfRecommendations = mappedPage?.rawRecommendations.slice(firstValidIndex) ?? [];
    }

    const filteredFirstRecommendationId = filterRecommendations(
      arrayOfRecommendations,
      enableFirstMatchSlideDisplay,
    )[0]?.pageId as string | undefined;
    const noMappingAndNoRecommendation = !mappedPage?.mapping && !filteredFirstRecommendationId;
    const isUserUnmatched = mappedPage?.mapping === 'USER_UNMATCHED';
    if (
      !mappedValues ||
      !mappedPage ||
      noMappingAndNoRecommendation ||
      isUserUnmatched
    ) return { ...acc, [pageId]: [] }
    if (!mappedPage.mapping) {
      return {
        ...acc,
        [pageId]: [filteredFirstRecommendationId!],
      }
    } else {
      return {
        ...acc,
        [pageId] : [mappedPage.mapping],
      }
    }
  }, {})
}

/**
 * Helper to check if the first rawRecommendation and recommendation are both defined and equal.
 */
function areFirstRecommendationsSame(mappedPage: MappingType | undefined): boolean {
  const firstRawRecommendationId = mappedPage?.rawRecommendations?.[0]?.pageId;
  const firstRecommendationId = mappedPage?.recommendations?.[0]?.pageId;
  return (
    firstRawRecommendationId !== undefined &&
    firstRecommendationId !== undefined &&
    firstRawRecommendationId === firstRecommendationId
  );
}

/**
 * TODO: This function should be removed as soon BEAC_7165_avoid_duplicate_matches_in_slide_version_tracking be stable
  * This function will calculate targetItems, determines what should be displays in the UI.
  * @param currentDocumentVersionORM
  * @param mappedValues
  * @return targetItems
*/
export const calculateDisplaySlides = (
  currentDocumentVersionORM: DocumentVersionORM,
  mappedValues: MappedValues,
  enableFirstMatchSlideDisplay: boolean,
) => {
  logger.versioning.settings.debug('calculateDisplaySlides featureflag BEAC_7165_ turned off');
  return currentDocumentVersionORM?.relations.pages.reduce<Record<string, string[]>>((acc, page) => {
    const pageId = page.model.pageId;
    const mappedPage = mappedValues[pageId] as MappingType | undefined;
    const firstRecommendationId = filterRecommendations(
      mappedPage?.rawRecommendations ?? [],
      enableFirstMatchSlideDisplay,
    )[0]?.pageId as string | undefined;
    const noMappingAndNoRecommendation = !mappedPage?.mapping && !firstRecommendationId;
    const isUserUnmatched = mappedPage?.mapping === 'USER_UNMATCHED';
    if (
      !mappedValues ||
      !mappedPage ||
      noMappingAndNoRecommendation ||
      isUserUnmatched
    ) return { ...acc, [pageId]: [] }
    if (!mappedPage.mapping) {
      return {
        ...acc,
        [pageId]: [firstRecommendationId!],
      }
    } else {
      return {
        ...acc,
        [pageId] : [mappedPage.mapping],
      }
    }
  }, {})
}

/**
  * The purpose of this function is to remove a specific string in duplicate Slides
  * array when user un-match a slide
*/
export const removeFromDuplicateSlides = (
  matchSlideStates: MatchSlideStatesType,
  stringToRemove: string,
  keys: string[],
  latestContentPageData: ContentPageData[],
) => {
  const updatedState = { ...matchSlideStates };
  keys.forEach(key => {
    if (updatedState[key]) {
      const duplicateSlides = matchSlideStates[key].duplicateSlides?.filter(pageId => pageId !== stringToRemove) || [];
      const mapping = latestContentPageData
        ?.find(pageData => pageData.pageId === key)?.mapping;

      // If there is no mapping, it could be below threshold or has too similar recommendations scores
      const updatedStatus = duplicateSlides.length || !mapping
        ? MATCH_SLIDE_STATUS.NEEDS_REVIEW
        : MATCH_SLIDE_STATUS.MATCHED;

      updatedState[key] = {
        status: updatedStatus,
        ...( duplicateSlides.length ? { duplicateSlides } : undefined),
      }
    }
  })
  return updatedState;
};

/**
  * The purpose of this function is to add a specific string to the duplicateSlides
  * array for the specified keys when a slide is matched.
*/
export const addToDuplicateSlides = (
  matchSlideStates: MatchSlideStatesType,
  stringToAdd: string,
  keys: string[],
) => {
  const updatedState = { ...matchSlideStates };
  keys.forEach(key => {
    if (updatedState[key]) {
      const duplicateSlides = updatedState[key].duplicateSlides || [];
      // Only add the string if it’s not already in the array
      if (!duplicateSlides.includes(stringToAdd)) {
        const updatedSlides = [...duplicateSlides, stringToAdd];
        updatedSlides.sort((a, b) => {
          const pageA = parseInt(a.split('_').pop() ?? '0', 10);
          const pageB = parseInt(b.split('_').pop() ?? '0', 10);
          return pageA - pageB;
        });
        updatedState[key] = {
          status: MATCH_SLIDE_STATUS.NEEDS_REVIEW,
          duplicateSlides: updatedSlides,
        };
      }
    }
  });

  return updatedState;
};

/**
  * The purpose of this function is to check if a specific itemId (previous version id) has
  * been used in groupedTargetItems, if so we will return the current version id in an array
*/
export const findKeysByItemId = (
  groupedTargetItems: GroupedTargetItems,
  targetItemId: string,
) => {
  const result: string[] = [];
  for (const [key, value] of Object.entries(groupedTargetItems)) {
    const found = value.some(item => item.itemId === targetItemId);
    if (found) {
      result.push(key);
    }
  }
  return result;
};

/**
  * The purpose of this function is to clear out any mapping in contentPageData if it has been duplicated
  * and assign them as USER_UNMATCHED. This should be called at publish state.
*/
export const dedupePageDataMapping = (
  contentPageData: ContentPageData[],
  matchSlideStates: MatchSlideStatesType,
) => {
  return contentPageData.map(pageData => {
    const { pageId } = pageData;
    if (matchSlideStates[pageId].status === MATCH_SLIDE_STATUS.NEEDS_REVIEW) {
      return {
        ...pageData,
        mapping: 'USER_UNMATCHED',
      }
    } else return pageData;
  })
};

/**
* This function calculates and applies slide settings from the previously published version to
* the current draft, based on the following rules:
* 1. Apply slide-level required slides to individual slides for every required slide that has
*    been matched, and drop those that have not.
* 2. Apply presentation-level required slides to entire presentation when all required slides
*    have a match.
* 3. Carry forward slide groups when all slides within the group have a match UNLESS slide
*    groups are imported, in which case the imported ones take precedence.
    *  - Groups will carry forward containing the equivalent slide from the previous version as
    *    long as all slides have an identified match by the system.
* 4. Apply publisher-edited speaker notes & slide titles when the newly uploaded document does
*    not have any speaker notes and/or slide titles.
*/
export const calculatesImportSlideSettingsFromPreviousVersion = (
  matchSlideStates: MatchSlideStatesType,
  currentVersionPageGroups?: PageGroup[],
  latestContentPageData?: ContentPageData[],
  latestPublishedDocumentVersionPageGroups?: PageGroup[],
  latestPublishedDocumentVersionPages?: Page[],
  latestPublishedContentPageData?: ContentPageData[],
) => {
  logger.versioning.settings.debug('Import slide settings from previous version...')
  // Reverse Mapping is a mapping from last version -> current version
  const reverseMapping = latestContentPageData?.reduce<Record<string, string>>((acc, pageData) => {
    const matchedStatus = matchSlideStates[pageData.pageId]?.status === MATCH_SLIDE_STATUS.MATCHED;
    if (matchedStatus && pageData.mapping) return { ...acc, [pageData.mapping]: pageData.pageId };
    else return acc;
  }, {}) ?? {};
  const latestPublishedGroups = latestPublishedDocumentVersionPageGroups ?? [];
  const latestPublishedPages = latestPublishedDocumentVersionPages ?? [];
  const lastVersionPresentationLevelRequiredSlideIds = latestPublishedPages.reduce<string[]>((acc, page) => {
    if (page.isRequired) return [...acc, page.pageId];
    else return acc;
  }, []);

  // STEP 1: Slide-level required slides
  logger.versioning.settings.debug('Calculating slide-level required slides...')
  const linkedSlidesMapping = latestPublishedPages.reduce<Record<string, string[]>>((acc, page) => {
    const hasLinkSlides = !!page.linkedSlides?.length;
    const parentSlideHasMapping = !!reverseMapping[page.pageId];
    const newLinkedSlides = page.linkedSlides?.reduce<string[]>((acc, pageId) => {
      const hasMapping = !!reverseMapping[pageId];
      if (hasMapping) return [...acc, reverseMapping[pageId]];
      else return acc;
    }, []) ?? [];
    if (hasLinkSlides && parentSlideHasMapping && newLinkedSlides.length) {
      const parentSlideId = reverseMapping[page.pageId];
      return { ...acc, [parentSlideId]: newLinkedSlides };
    }
    else return acc;
  }, {});
  logger.versioning.settings.debug('The new slide-level required slides: ', linkedSlidesMapping)

  // STEP 2: Presentation-level required slides
  logger.versioning.settings.debug('Calculating presentation-level required slides...')
  const shouldApplyPresentationLevelRequiredSlide = lastVersionPresentationLevelRequiredSlideIds
    .every(pageId => reverseMapping[pageId])
  const requiredSlidePageIds = shouldApplyPresentationLevelRequiredSlide
    ? lastVersionPresentationLevelRequiredSlideIds.map(lastVersionPageId => reverseMapping[lastVersionPageId])
    : [];
  logger.versioning.settings.debug('The new presentation-level required slides: ', requiredSlidePageIds)

  // STEP 3: Carry forward slide groups
  logger.versioning.settings.debug('Calculating slide groups...')
  const currentImportedGroups = currentVersionPageGroups
    ?.filter(group => group.source === PageGroupSource.DOCUMENT) ?? [];
  const newGroups = currentImportedGroups.length
    ? currentImportedGroups
    : latestPublishedGroups.reduce<PageGroup[]>((acc, pageGroup) => {
      const allSlidesHasMapping = !!pageGroup.pageIds?.every(pageId => !!reverseMapping[pageId]);
      if (allSlidesHasMapping) {
        const newPageIds = pageGroup.pageIds?.map(pageId => reverseMapping[pageId]);
        const newPageGroup: PageGroup = {
          id: uuid(),
          pageIds: newPageIds,
          name: pageGroup.name,
          locked: pageGroup.locked,
          source: pageGroup.source,
          sourceID: pageGroup.sourceID,
        };
        return [...acc, newPageGroup];
      }
      else return acc;
    }, []);
  logger.versioning.settings.debug('The new slide groups: ', newGroups)

  // STEP 4: Carry forward speaker notes & slide titles
  logger.versioning.settings.debug('Calculating carry-forward speaker notes & slide titles...')
  const newContentPageData: ContentPageData[] | undefined = latestContentPageData?.map(pageData => {
    const lastVersionPageData = latestPublishedContentPageData
      ?.find(pd => pd.pageId === pageData.mapping)
    const hasNoOriginalTitle = !pageData.originalTitle;
    const hasNoOriginalSpeakerNotes = !pageData.originalSpeakerNotes;
    const matchedStatus = matchSlideStates[pageData.pageId]?.status === MATCH_SLIDE_STATUS.MATCHED;
    const shouldApplyLastVersionTitle = matchedStatus &&
      pageData.mapping &&
      lastVersionPageData &&
      hasNoOriginalTitle;
    const shouldApplyLastVersionSpeakerNotes = matchedStatus &&
      pageData.mapping &&
      lastVersionPageData &&
      hasNoOriginalSpeakerNotes;
    // Assigning speaker notes & slide titles
    return {
      ...pageData,
      title: shouldApplyLastVersionTitle
        ? lastVersionPageData.title
        : pageData.originalTitle,
      speakerNotes: shouldApplyLastVersionSpeakerNotes
        ? lastVersionPageData.speakerNotes
        : pageData.originalSpeakerNotes,
    }
  })

  return {
    newContentPageData,
    linkedSlidesMapping,
    requiredSlidePageIds,
    newGroups,
  };
}

export const filterDedupedRecommendations = (
  dedupedRecommendations: Array<{ score: number; pageId: string, strategy?: string }>,
  originalRecommendations: Array<{ score: number; pageId: string, strategy?: string }>,
  enableFirstMatchSlideDisplay: boolean = false,
) => {
  const firstDedupedRecommendationId = dedupedRecommendations[0]?.pageId;
  const firstOriginalRecommendationId = originalRecommendations[0]?.pageId;

  if (firstDedupedRecommendationId === firstOriginalRecommendationId) {
    return filterRecommendations(originalRecommendations, enableFirstMatchSlideDisplay);
  }

  const indexFirstValidRecommendation =
    originalRecommendations.findIndex(rec => rec.pageId === firstDedupedRecommendationId);

  if (indexFirstValidRecommendation === -1) {
    return filterRecommendations([], enableFirstMatchSlideDisplay);
  }

  const validRecommendations = originalRecommendations.slice(indexFirstValidRecommendation);
  return filterRecommendations(validRecommendations, enableFirstMatchSlideDisplay);
}

export const filterRecommendations = (
  recommendations: Array<{ score: number; pageId: string, strategy?: string }>,
  enableFirstMatchSlideDisplay: boolean = false,
  differenceThreshold: number = RECOMMENDATION_DIFFERENCE_THRESHOLD,
) => {
  if (!enableFirstMatchSlideDisplay) {
    return recommendations.filter(recommendation => recommendation.score >= NO_RECOMMENDATION_SCORE_THRESHOLD);
  }

  return recommendations.filter((recommendation, index) => {
    const score = recommendation.score;
    if (score >= IS_WARNING_THRESHOLD) {
      return true;
    }

    if (recommendations.length === 1 && score > NO_RECOMMENDATION_SCORE_THRESHOLD && score < IS_WARNING_THRESHOLD) {
      return true;
    }

    // Only check difference for the first recommendation
    if (enableFirstMatchSlideDisplay && index === 0 && recommendations.length > 1) {
      const firstScore = score;

      if (firstScore > NO_RECOMMENDATION_SCORE_THRESHOLD) {
        return true;
      }

      if (firstScore <= FIRST_MATCH_SLIDE_DISPLAY_DISABLED_THRESHOLD) {
        return false;
      }

      const nextRecommendation = recommendations[1];

      const scoreDifference = (firstScore - nextRecommendation.score).toFixed(10);
      // We only include the first recommendation if the difference is greater than the threshold
      return Number(scoreDifference) >= differenceThreshold;
    }

    // Include all other recommendations that meet the threshold in this case we want to use the original approach NO_RECOMMENDATION_SCORE_THRESHOLD
    return score >= NO_RECOMMENDATION_SCORE_THRESHOLD;
  });
};

export default {
  getDocumentORMFactory,
  omitInternalFields,
  determineSemVerChange,
  getAvailabletabs,
  calculateContentPageDataAndMatchSlideStates,
  calculateDedupedContentPageDataAndMatchSlideStates,
  calculateDisplaySlides,
  calculateDedupedDisplaySlides,
  removeFromDuplicateSlides,
  addToDuplicateSlides,
  findKeysByItemId,
  dedupePageDataMapping,
  calculatesImportSlideSettingsFromPreviousVersion,
}
